import { DependencyList, useEffect, useRef } from 'react';

interface UseWindowEventListenerOptions {
  capture?: boolean;
}

const toEventNames = (
  eventName: string | readonly string[]
): readonly string[] => {
  if (typeof eventName === 'string') {
    return [eventName];
  }

  return eventName;
};

const getEventNamesKey = (eventName: string | readonly string[]): string =>
  toEventNames(eventName).join('\0');

export function useWindowEventListener(
  eventName: string | readonly string[],
  handler: (event: Event) => void,
  deps: DependencyList = [],
  options: UseWindowEventListenerOptions = {}
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const { capture = false } = options;

  useEffect(() => {
    const eventNames = toEventNames(eventName);
    const listener = (event: Event) => {
      handlerRef.current(event);
    };

    eventNames.forEach((name) => {
      window.addEventListener(name, listener, capture);
    });

    return () => {
      eventNames.forEach((name) => {
        window.removeEventListener(name, listener, capture);
      });
    };
  }, [capture, getEventNamesKey(eventName), ...deps]);
}
